import { ChartBarIcon } from '@phosphor-icons/react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo, useState } from 'react';

import { EmptyState } from '@/components/empty-state';
import { logbookClassLabel } from '@/features/aircraft';
import { useSetting } from '@/features/settings';
import type { LogbookClass } from '@/lib/db';
import { logbookEntries } from '@/lib/db';
import { formatLocalDate } from '@/lib/format';

import {
  confirmedOnly,
  filterDateRange,
  filterLast90Days,
  filterRolling12Months,
  GROUP_BY,
  type GroupTotal,
  groupTotals,
  sumTotals,
} from './aggregate';
import { type Caa1373Row, type CaaCapacity, sumCaa1373 } from './caa1373';
import { OpeningBalanceField, PendingDrafts } from './PendingDrafts';
import { formatDecimalHours } from './time';

/**
 * Totals and grouping — `PLAN.md` §6 Phase 6, `NZ-LOGBOOK.md` §4: "every total
 * is a fold over a filtered entry set... build one aggregation engine and
 * expose it everywhere." This page is `aggregate.ts` read four ways —
 * grand totals, then the same fold grouped by registration, type and class —
 * over whichever period the pilot picks, so the sum-of-groups invariant
 * `aggregate.test.ts` checks is visible on screen, not just in a test file.
 *
 * Only confirmed entries count, same as the running total on the logbook
 * grid itself — a draft has not been reviewed yet and does not belong in a
 * number a pilot might quote.
 */

type Period = 'all' | 'year' | 'last90' | 'rolling12';

const PERIODS: readonly { id: Period; label: string }[] = [
  { id: 'all', label: 'All time' },
  { id: 'year', label: 'This calendar year' },
  { id: 'last90', label: 'Last 90 days' },
  { id: 'rolling12', label: 'Rolling 12 months' },
];

function GroupTable({
  title,
  columnLabel,
  groups,
  labelFor,
}: {
  title: string;
  columnLabel: string;
  groups: readonly GroupTotal<string>[];
  labelFor?: (key: string) => string;
}) {
  if (groups.length === 0) {
    return null;
  }
  return (
    <section className="border-border bg-surface rounded-container border p-4">
      <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
      {/*
        `table-fixed` plus a shared `colgroup` — every `GroupTable` on this
        page sits in a same-width card, but each was sizing its columns off
        its own longest cell (`Single-engine land` versus `ZK-DFH`), so the
        same "Flights" header landed at a different x-position table to
        table. Fixed proportional widths make every instance size itself the
        same way, so the numeric columns line up down the whole page.
      */}
      <table className="tabular mt-3 w-full table-fixed border-collapse text-sm">
        <colgroup>
          <col className="w-[36%]" />
          <col className="w-[16%]" />
          <col className="w-[14%]" />
          <col className="w-[12%]" />
          <col className="w-[11%]" />
          <col className="w-[11%]" />
        </colgroup>
        <thead>
          <tr className="border-border text-muted-foreground border-b text-xs">
            <th className="py-1 text-left font-normal">{columnLabel}</th>
            <th className="py-1 pl-2 text-right font-normal">Flights</th>
            <th className="py-1 pl-2 text-right font-normal">Total</th>
            <th className="py-1 pl-2 text-right font-normal">PIC</th>
            <th className="py-1 pl-2 text-right font-normal">Dual</th>
            <th className="py-1 pl-2 text-right font-normal">Night</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <tr key={group.key} className="border-border border-b last:border-0">
              <td className="truncate py-1">{labelFor ? labelFor(group.key) : group.key}</td>
              <td className="py-1 pl-2 text-right">{group.totals.flightCount}</td>
              <td className="py-1 pl-2 text-right">{formatDecimalHours(group.totals.totalHr)}</td>
              <td className="py-1 pl-2 text-right">{formatDecimalHours(group.totals.picHr)}</td>
              <td className="py-1 pl-2 text-right">{formatDecimalHours(group.totals.dualHr)}</td>
              <td className="py-1 pl-2 text-right">{formatDecimalHours(group.totals.nightHr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/**
 * The sixteen CAA 1373 hour columns, added up.
 *
 * A pilot filling in a licence application is copying these figures, not
 * the capacity totals above them: "single-engine day PIC" is a box on the
 * form, and working it out by hand from Day, Night and PIC is exactly the
 * arithmetic this logbook exists to do. Blank where a cell is empty, the
 * same as the printed page — a grid of sixteen zeroes is not a total
 * anyone reads.
 */
const CAA_CAPACITY_ROWS: readonly { id: CaaCapacity; label: string }[] = [
  { id: 'dual', label: 'Dual' },
  { id: 'pic', label: 'PIC' },
  { id: 'coPilot', label: 'Co-Pilot' },
  { id: 'commandPractice', label: 'Command Practice' },
];

function Caa1373Totals({ totals }: { totals: Caa1373Row }) {
  const cell = (hours: number) => (hours === 0 ? '—' : formatDecimalHours(hours));
  return (
    <section className="border-border bg-surface rounded-container border p-4">
      <h2 className="text-sm font-semibold tracking-tight">CAA 1373 columns</h2>
      <table className="tabular mt-3 w-full border-collapse text-sm">
        <thead>
          <tr className="border-border text-muted-foreground border-b text-xs">
            <th className="py-1 text-left font-normal" />
            <th className="py-1 text-right font-normal" colSpan={2}>
              Single Engine
            </th>
            <th className="border-border py-1 text-right font-normal" colSpan={2}>
              Multi Engine
            </th>
          </tr>
          <tr className="border-border text-muted-foreground border-b text-xs">
            <th className="py-1 text-left font-normal">Capacity</th>
            <th className="py-1 text-right font-normal">Day</th>
            <th className="py-1 text-right font-normal">Night</th>
            <th className="py-1 text-right font-normal">Day</th>
            <th className="py-1 text-right font-normal">Night</th>
          </tr>
        </thead>
        <tbody>
          {CAA_CAPACITY_ROWS.map((row) => (
            <tr key={row.id} className="border-border border-b last:border-0">
              <td className="py-1">{row.label}</td>
              <td className="py-1 text-right">{cell(totals.single[row.id].day)}</td>
              <td className="py-1 text-right">{cell(totals.single[row.id].night)}</td>
              <td className="py-1 text-right">{cell(totals.multi[row.id].day)}</td>
              <td className="py-1 text-right">{cell(totals.multi[row.id].night)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {totals.unplacedHr === 0 ? null : (
        <p className="text-muted-foreground mt-2 text-xs">
          {formatDecimalHours(totals.unplacedHr)} hours of single-engine Co-Pilot or Command
          Practice time are not in this table: CAA 1373 prints those columns under MULTI ENGINE
          only. They are still in the total time above.
        </p>
      )}
    </section>
  );
}

export function TotalsPage() {
  const entries = useLiveQuery(() => logbookEntries.listOrdered(), [], null);
  const [timeZone] = useSetting('timeZone');
  const [period, setPeriod] = useState<Period>('all');

  const asOf = formatLocalDate(new Date().toISOString(), { format: 'iso', timeZone });
  const confirmed = useMemo(() => (entries === null ? [] : confirmedOnly(entries)), [entries]);

  const filtered = useMemo(() => {
    switch (period) {
      case 'year':
        return filterDateRange(confirmed, `${asOf.slice(0, 4)}-01-01`, asOf);
      case 'last90':
        return filterLast90Days(confirmed, asOf);
      case 'rolling12':
        return filterRolling12Months(confirmed, asOf);
      default:
        return confirmed;
    }
  }, [confirmed, period, asOf]);

  const totals = useMemo(() => sumTotals(filtered), [filtered]);
  const caaTotals = useMemo(() => sumCaa1373(filtered), [filtered]);
  const byRegistration = useMemo(() => groupTotals(filtered, GROUP_BY.registration), [filtered]);
  const byType = useMemo(() => groupTotals(filtered, GROUP_BY.aircraftType), [filtered]);
  const byClass = useMemo(() => groupTotals(filtered, GROUP_BY.engineClass), [filtered]);
  const byYear = useMemo(() => groupTotals(confirmed, GROUP_BY.year), [confirmed]);

  if (entries === null) {
    return <p className="text-muted-foreground text-sm">Opening the local database…</p>;
  }

  if (confirmed.length === 0) {
    return (
      <div className="space-y-4">
        <OpeningBalanceField />
        <EmptyState
          icon={ChartBarIcon}
          title="No approved entries"
          description={
            <>
              Totals include approved entries only.
              <PendingDrafts className="mt-2" />
            </>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <OpeningBalanceField />
        <PendingDrafts />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => {
              setPeriod(option.id);
            }}
            className={`rounded-control border px-3 py-1.5 text-sm transition-colors duration-150 ${
              period === option.id
                ? 'border-accent bg-accent-soft text-foreground'
                : 'border-border bg-surface text-muted-foreground hover:text-foreground'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-muted-foreground text-sm">No approved entries fall in this period.</p>
      ) : (
        <>
          <section className="border-border bg-surface rounded-container border p-4">
            <h2 className="text-sm font-semibold tracking-tight">Grand total</h2>
            <dl className="figure mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-muted-foreground text-xs">Total time</dt>
                <dd className="font-semibold">{formatDecimalHours(totals.totalHr)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">PIC</dt>
                <dd>{formatDecimalHours(totals.picHr)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Dual</dt>
                <dd>{formatDecimalHours(totals.dualHr)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Night</dt>
                <dd>{formatDecimalHours(totals.nightHr)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Co-Pilot</dt>
                <dd>{formatDecimalHours(totals.coPilotHr)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Command Practice</dt>
                <dd>{formatDecimalHours(totals.commandPracticeHr)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Instrument (actual)</dt>
                <dd>{formatDecimalHours(totals.actualInstrumentHr)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Instrument (simulated)</dt>
                <dd>{formatDecimalHours(totals.simulatedInstrumentHr)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Instrument (ground)</dt>
                <dd>{formatDecimalHours(totals.groundInstrumentHr)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Approaches (non-prec. / prec.)</dt>
                <dd>
                  {totals.approachesNonPrecision} / {totals.approachesPrecision}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Flight Instructor</dt>
                <dd>{formatDecimalHours(totals.instructorHr)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">IFR Cross Country</dt>
                <dd>{formatDecimalHours(totals.ifrCrossCountryHr)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Takeoffs (day / night)</dt>
                <dd>
                  {totals.takeoffsDay} / {totals.takeoffsNight}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Landings (day / night)</dt>
                <dd>
                  {totals.landingsDay} / {totals.landingsNight}
                </dd>
              </div>
            </dl>
          </section>

          <Caa1373Totals totals={caaTotals} />

          <GroupTable
            title="By aircraft registration"
            columnLabel="Registration"
            groups={byRegistration}
          />
          <GroupTable title="By aircraft type" columnLabel="Type" groups={byType} />
          <GroupTable
            title="By class"
            columnLabel="Class"
            groups={byClass}
            labelFor={(key) => logbookClassLabel(key as LogbookClass)}
          />
        </>
      )}

      <GroupTable title="By year (all time)" columnLabel="Year" groups={byYear} />
    </div>
  );
}
